/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo } from 'react'

import { Button, DatePicker, Dropdown, type DropdownOption } from '@/components/ui'
import { AddressAutocomplete, type GeocodedPlace } from '@/components/ui/address-autocomplete'
import { useDashboard } from '@/hooks'
import useForm from '@/hooks/useForm'
import { cachedTrackerMapCompanyId } from '@/pages/trackers/map/TrackerMapContext'
import { useCustomers } from '@/pages/trackers/map/useCustomers'
import { tempUnitSuffix, userToCelsius } from '@/utils/conversions'

import { AddressCoordinateWarning } from './AddressCoordinateWarning'
import { TrackerCommitments } from './TrackerCommitments'
import { SHIPMENT_STATUSES, type CreateShipmentBody, type ShipmentFormValues, type ShipmentStatus } from './types'
import { useTrackerList } from './useTrackerList'
import { useTrackerShipments } from './useTrackerShipments'
import { shipmentValidators } from './validators'

const INITIAL_VALUES: ShipmentFormValues = {
  shipmentName: '',
  customerId: null,
  trackerId: null,
  status: 'planned',
  expectedStartAt: null,
  expectedEndAt: null,
  actualStartAt: null,
  actualEndAt: null,
  originName: '',
  originAddress: '',
  originLocation: '',
  originLatitude: null,
  originLongitude: null,
  originAddressEdited: false,
  destinationName: '',
  destinationAddress: '',
  destinationLocation: '',
  destinationLatitude: null,
  destinationLongitude: null,
  destinationAddressEdited: false,
  minTemperature: '',
  maxTemperature: '',
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="card bg-base-100 shadow">
    <div className="card-body gap-4">
      <h2 className="card-title text-base">{title}</h2>
      {children}
    </div>
  </section>
)

const Field: React.FC<{
  label: string
  htmlFor?: string
  required?: boolean
  error?: string
  hint?: string
  children: React.ReactNode
}> = ({ label, htmlFor, required, error, hint, children }) => (
  <div className="form-control w-full">
    <label htmlFor={htmlFor} className="label-text mb-1 block">
      {label} {required && <span className="text-error">*</span>}
    </label>
    {children}
    {hint && !error && <span className="label-text-alt mt-1 block text-base-content/60">{hint}</span>}
    {error && <span className="label-text-alt mt-1 block text-error">{error}</span>}
  </div>
)

export interface ShipmentFormProps {
  isSaving: boolean
  onSubmit: (body: CreateShipmentBody) => void
  /** Ask the page to discard this form; it remounts a fresh one. */
  onClear: () => void
  /** Shown when validation blocks a submit, so the page can clear its own alerts. */
  onInvalid: () => void
}

/**
 * The new-shipment fields. Deliberately remounted (via a key on the page) rather
 * than reset in place: useForm's setValues re-runs every validator, so resetting
 * a completed form would light up every required-field error at once.
 */
export const ShipmentForm: React.FC<ShipmentFormProps> = ({ isSaving, onSubmit, onClear, onInvalid }) => {
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const unitSuffix = tempUnitSuffix(units)

  const [onValueChange, validateFields, errors, values] = useForm<ShipmentFormValues>(
    // Default to the company picked on the tracker map, when this tab has one.
    { ...INITIAL_VALUES, customerId: cachedTrackerMapCompanyId() ?? null },
    shipmentValidators
  )
  const { customers, isLoading: customersLoading } = useCustomers()
  const { trackers, isLoading: trackersLoading, error: trackersError } = useTrackerList(values.customerId)
  const {
    shipments: trackerShipments,
    isLoading: trackerShipmentsLoading,
    error: trackerShipmentsError,
  } = useTrackerShipments(values.customerId, values.trackerId)

  const customerOptions: DropdownOption[] = useMemo(
    () => customers.map((c) => ({ value: String(c.id), label: c.name })),
    [customers]
  )
  // Just the device name — whatever it is currently committed to is spelled out
  // properly below the field once one is picked, rather than crammed in here.
  const trackerOptions: DropdownOption[] = useMemo(
    () => trackers.map((t) => ({ value: String(t.id), label: t.label })),
    [trackers]
  )
  const statusOptions: DropdownOption[] = SHIPMENT_STATUSES.map((s) => ({ value: s.value, label: s.label }))
  const selectedTracker = trackers.find((t) => t.id === values.trackerId) ?? null

  // Entering a shipment that has already started or finished is how history gets
  // backfilled, so the actual times are required exactly when the status implies
  // them. The server enforces the same pairing.
  const actualStartRequired = values.status === 'active' || values.status === 'completed'
  const actualEndRequired = values.status === 'completed'
  const actualStartHint = actualStartRequired
    ? `Required for ${values.status === 'active' ? 'an active' : 'a completed'} shipment`
    : values.status === 'planned'
      ? 'Leave blank until it starts'
      : 'Optional'
  const actualEndHint = actualEndRequired
    ? 'Required for a completed shipment'
    : values.status === 'cancelled'
      ? 'Optional'
      : 'Leave blank until it ends'

  const selected = (options: DropdownOption[], value: string | null) =>
    options.find((option) => option.value === value) ?? null

  // Several fields move together here, but each goes through onValueChange
  // rather than setValues so only the field being touched gets validated —
  // otherwise typing an address surfaces every other field's error. The
  // coordinates go first: the location validator reads them off the previous
  // values, and useForm applies these updates in order.
  const handlePlace = (prefix: 'origin' | 'destination') => (place: GeocodedPlace) => {
    onValueChange(`${prefix}Latitude`, place.latitude)
    onValueChange(`${prefix}Longitude`, place.longitude)
    onValueChange(`${prefix}Location`, place.address)
    // The pick is what produced the coordinates, so its address replaces
    // whatever was there — including an earlier pick's. Hand-editing afterwards
    // is what the mismatch warning is for.
    onValueChange(`${prefix}Address`, place.address)
    onValueChange(`${prefix}AddressEdited`, false)
    // The name is the site, not the address, so only fill it while still blank.
    if (!values[`${prefix}Name`]) onValueChange(`${prefix}Name`, place.address.split(',')[0])
  }

  /** Editing the text invalidates the point that was picked for it. */
  const handleLocationText = (prefix: 'origin' | 'destination') => (text: string) => {
    onValueChange(`${prefix}Latitude`, null)
    onValueChange(`${prefix}Longitude`, null)
    onValueChange(`${prefix}Location`, text)
  }

  const handleSubmit = async () => {
    let validated: ShipmentFormValues
    try {
      validated = await validateFields()
    } catch {
      onInvalid()
      return
    }

    onSubmit({
      shipmentName: validated.shipmentName.trim(),
      customerId: validated.customerId as number,
      trackerId: validated.trackerId,
      status: validated.status,
      expectedStartAt: (validated.expectedStartAt as Date).toISOString(),
      expectedEndAt: (validated.expectedEndAt as Date).toISOString(),
      actualStartAt: validated.actualStartAt?.toISOString() ?? null,
      actualEndAt: validated.actualEndAt?.toISOString() ?? null,
      originName: validated.originName.trim(),
      originAddress: validated.originAddress.trim() || null,
      originLatitude: validated.originLatitude as number,
      originLongitude: validated.originLongitude as number,
      destinationName: validated.destinationName.trim(),
      destinationAddress: validated.destinationAddress.trim() || null,
      destinationLatitude: validated.destinationLatitude as number,
      destinationLongitude: validated.destinationLongitude as number,
      minTemperatureCelsius: userToCelsius(Number(validated.minTemperature), 1, units),
      maxTemperatureCelsius: userToCelsius(Number(validated.maxTemperature), 1, units),
    })
  }

  return (
    <div className="grid gap-4">
      <Section title="Shipment information">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Customer" required error={errors.customerId}>
            <Dropdown
              id="shipment-customer"
              options={customerOptions}
              selectedItem={selected(customerOptions, values.customerId === null ? null : String(values.customerId))}
              handleSingleSelectItem={(option) => {
                onValueChange('customerId', option ? Number(option.value) : null)
                // Trackers are scoped to the customer, so a change drops the pick.
                onValueChange('trackerId', null)
              }}
              placeholder={customersLoading ? 'Loading…' : 'Select customer'}
              animateDots={customersLoading}
            />
          </Field>

          <Field
            label="Shipment ID"
            htmlFor="shipment-name"
            required
            error={errors.shipmentName}
            hint="A number or a name"
          >
            <input
              id="shipment-name"
              className={`input input-bordered w-full ${errors.shipmentName ? 'input-error' : ''}`}
              value={values.shipmentName}
              onChange={(e) => onValueChange('shipmentName', e.target.value)}
              placeholder="e.g. Dallas DC to Chicago Store"
              data-testid="shipment-name"
            />
          </Field>

          <Field label="Status" required>
            <Dropdown
              id="shipment-status"
              options={statusOptions}
              selectedItem={selected(statusOptions, values.status)}
              handleSingleSelectItem={(option) =>
                onValueChange('status', (option?.value ?? 'planned') as ShipmentStatus)
              }
              isClearable={false}
            />
          </Field>

          <Field
            label="Tracking device (optional)"
            error={trackersError}
            hint={values.customerId ? 'Can be assigned later' : 'Pick a customer first'}
          >
            <Dropdown
              id="shipment-tracker"
              options={trackerOptions}
              selectedItem={selected(trackerOptions, values.trackerId === null ? null : String(values.trackerId))}
              handleSingleSelectItem={(option) => onValueChange('trackerId', option ? Number(option.value) : null)}
              placeholder={trackersLoading ? 'Loading…' : 'Select device'}
              animateDots={trackersLoading}
              noOptionsMessage={values.customerId ? 'No devices for this customer' : 'Select a customer first'}
            />
          </Field>
        </div>

        {/* What the chosen device is already committed to, plus a clash warning
            once times are entered. Advisory only — see TrackerCommitments. */}
        {selectedTracker && (
          <TrackerCommitments
            trackerLabel={selectedTracker.label}
            shipments={trackerShipments}
            isLoading={trackerShipmentsLoading}
            error={trackerShipmentsError}
            proposedStart={values.expectedStartAt}
            proposedEnd={values.expectedEndAt}
          />
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        {(['origin', 'destination'] as const).map((prefix) => (
          <Section key={prefix} title={prefix === 'origin' ? 'Origin' : 'Destination'}>
            <Field
              label={`${prefix === 'origin' ? 'Origin' : 'Destination'} name`}
              htmlFor={`${prefix}-name`}
              required
              error={errors[`${prefix}Name`]}
            >
              <input
                id={`${prefix}-name`}
                className={`input input-bordered w-full ${errors[`${prefix}Name`] ? 'input-error' : ''}`}
                value={values[`${prefix}Name`]}
                onChange={(e) => onValueChange(`${prefix}Name`, e.target.value)}
                placeholder={prefix === 'origin' ? 'e.g. Sysco Louisville' : 'e.g. Dawn Foods DC Jackson'}
                data-testid={`${prefix}-name`}
              />
            </Field>

            <AddressAutocomplete
              id={`${prefix}-location`}
              label="Location"
              required
              value={values[`${prefix}Location`]}
              coordinates={{
                latitude: values[`${prefix}Latitude`],
                longitude: values[`${prefix}Longitude`],
              }}
              onChange={handleLocationText(prefix)}
              onSelect={handlePlace(prefix)}
              error={errors[`${prefix}Location`]}
              placeholder="Search for an address"
            />

            <Field label="Address" htmlFor={`${prefix}-address`} hint="Optional — filled from the location, editable">
              <input
                id={`${prefix}-address`}
                className="input input-bordered w-full"
                value={values[`${prefix}Address`]}
                onChange={(e) => {
                  onValueChange(`${prefix}Address`, e.target.value)
                  onValueChange(`${prefix}AddressEdited`, true)
                }}
                data-testid={`${prefix}-address`}
              />
              <AddressCoordinateWarning
                id={`${prefix}-address-warning`}
                show={values[`${prefix}AddressEdited`] && values[`${prefix}Latitude`] !== null}
              />
            </Field>
          </Section>
        ))}
      </div>

      <Section title="Schedule">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Expected start" required error={errors.expectedStartAt}>
            <DatePicker
              inputId="expected-start"
              shouldShowTime
              initialDate={null}
              handleSet={(date) => onValueChange('expectedStartAt', date)}
            />
          </Field>
          <Field label="Expected end" required error={errors.expectedEndAt}>
            <DatePicker
              inputId="expected-end"
              shouldShowTime
              initialDate={null}
              handleSet={(date) => onValueChange('expectedEndAt', date)}
            />
          </Field>
          {/* Which of these are needed follows the status — a shipment can be
              entered as already active or completed to backfill history. */}
          <Field
            label="Actual start"
            required={actualStartRequired}
            error={errors.actualStartAt}
            hint={actualStartHint}
          >
            <DatePicker
              inputId="actual-start"
              shouldShowTime
              initialDate={null}
              handleSet={(date) => onValueChange('actualStartAt', date)}
            />
          </Field>
          <Field label="Actual end" required={actualEndRequired} error={errors.actualEndAt} hint={actualEndHint}>
            <DatePicker
              inputId="actual-end"
              shouldShowTime
              initialDate={null}
              handleSet={(date) => onValueChange('actualEndAt', date)}
            />
          </Field>
        </div>
      </Section>

      <Section title="Temperature range">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label={`Min temperature (${unitSuffix})`} htmlFor="min-temp" required error={errors.minTemperature}>
            <input
              id="min-temp"
              type="number"
              inputMode="decimal"
              className={`input input-bordered w-full ${errors.minTemperature ? 'input-error' : ''}`}
              value={values.minTemperature}
              onChange={(e) => onValueChange('minTemperature', e.target.value)}
              data-testid="min-temp"
            />
          </Field>
          <Field label={`Max temperature (${unitSuffix})`} htmlFor="max-temp" required error={errors.maxTemperature}>
            <input
              id="max-temp"
              type="number"
              inputMode="decimal"
              className={`input input-bordered w-full ${errors.maxTemperature ? 'input-error' : ''}`}
              value={values.maxTemperature}
              onChange={(e) => onValueChange('maxTemperature', e.target.value)}
              data-testid="max-temp"
            />
          </Field>
        </div>
        {/* The labels follow the user's units, so say where the value actually lands. */}
        <p className="text-sm text-base-content/60">Stored in Celsius; shown here in your preferred units.</p>
      </Section>

      <div className="flex justify-end gap-2">
        <Button handleClick={onClear} variation="ghost" isDisabled={isSaving}>
          Clear
        </Button>
        <Button handleClick={() => void handleSubmit()} variation="primary" isDisabled={isSaving}>
          {isSaving ? (
            <>
              <span className="loading loading-spinner loading-xs" />
              Creating…
            </>
          ) : (
            'Create Shipment'
          )}
        </Button>
      </div>
    </div>
  )
}
